import { supabase } from './supabase';
import { notifyLoyalty } from './notify';

// Weekly visit streak — rules live in supabase-loyalty.sql (trigger on check-in).
// Keep these constants in sync with loyalty_sync_week().
export const LOYALTY_GOAL = 5;
export const LOYALTY_TIERS = [3, 5] as const;
export const LOYALTY_REWARD = 1500;

export interface LoyaltyWeek { week_start: string; visits: number; earned: number }
export interface LoyaltyUnseen { id: number; tier: number; amount: number; week_start: string }
export interface LoyaltyStatus {
  today: string;            // YYYY-MM-DD, Moscow
  week_start: string;       // Monday of the current week
  visits: number;           // distinct visit days this week
  visit_days: string[];     // YYYY-MM-DD
  week_rewards: number[];   // tiers already awarded this week
  streak: number;           // consecutive weeks with ≥ 3 visits
  history: LoyaltyWeek[];   // newest first, weeks with visits only
  unseen: LoyaltyUnseen[];  // rewards whose celebration wasn't shown yet
}
export interface LoyaltyOverviewRow { user_id: number; name: string; visits: number; earned: number }

export async function getLoyaltyStatus(userId: number): Promise<LoyaltyStatus> {
  const { data, error } = await supabase.rpc('get_loyalty_status', { p_user_id: userId });
  if (error) throw error;
  // Never let a malformed answer reach the UI — callers hide the card on throw.
  const d = data as Partial<LoyaltyStatus> | null;
  if (!d || typeof d.today !== 'string' || typeof d.week_start !== 'string' || typeof d.visits !== 'number') {
    throw new Error('Unexpected loyalty status');
  }
  return {
    today: d.today, week_start: d.week_start, visits: d.visits,
    visit_days: Array.isArray(d.visit_days) ? d.visit_days : [],
    week_rewards: Array.isArray(d.week_rewards) ? d.week_rewards : [],
    streak: typeof d.streak === 'number' ? d.streak : 0,
    history: Array.isArray(d.history) ? d.history : [],
    unseen: Array.isArray(d.unseen) ? d.unseen : [],
  };
}

export async function markLoyaltySeen(userId: number) {
  await supabase.rpc('loyalty_mark_seen', { p_user_id: userId });
}

export async function getLoyaltyWeekOverview(weekStart?: string): Promise<LoyaltyOverviewRow[]> {
  const { data, error } = await supabase.rpc('get_loyalty_week_overview', { p_week_start: weekStart ?? null });
  if (error) throw error;
  return (data as LoyaltyOverviewRow[]) || [];
}

/** Next tier still ahead this week, or null when the weekly goal is complete. */
export function nextTier(visits: number): number | null {
  return LOYALTY_TIERS.find(t => visits < t) ?? null;
}

/** Days left in the week including today (Mon → 7, Sun → 1). */
export function daysLeftInWeek(today: string, weekStart: string): number {
  const d = (s: string) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
  return Math.max(1, 7 - Math.round((d(today) - d(weekStart)) / 86400000));
}

/** Longest run of consecutive weeks with ≥ 3 visits in the loaded history. */
export function bestStreak(history: LoyaltyWeek[], current: number): number {
  const weeks = history.filter(w => w.visits >= 3).map(w => w.week_start).sort();
  let best = 0, run = 0, prev: number | null = null;
  for (const w of weeks) {
    const t = Date.parse(w);
    run = prev !== null && t - prev === 7 * 86400000 ? run + 1 : 1;
    best = Math.max(best, run);
    prev = t;
  }
  return Math.max(best, current);
}

export function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** Status-line text under the weekly track. */
export function loyaltyHint(s: Pick<LoyaltyStatus, 'visits' | 'today' | 'week_start'>): string {
  const next = nextTier(s.visits);
  const left = daysLeftInWeek(s.today, s.week_start);
  if (next === null) return 'Максимум недели собран — 3 000 HUcoin твои!';
  const need = next - s.visits;
  const daysTxt = left === 1 ? 'неделя заканчивается сегодня' : `до конца недели ${left} ${plural(left, 'день', 'дня', 'дней')}`;
  return `Ещё ${need} ${plural(need, 'визит', 'визита', 'визитов')} — и +${LOYALTY_REWARD.toLocaleString('ru-RU')} HUcoin · ${daysTxt}`;
}

/** Player notification after a new visit day was counted (Telegram + push). */
function checkInMessage(visits: number, streak: number): { text: string; title: string; body: string } | null {
  const amt = LOYALTY_REWARD.toLocaleString('ru-RU');
  switch (visits) {
    case 1: return {
      title: '🔥 Неделя началась!',
      body: `Визит 1/5. Ещё 2 — и +${amt} HUcoin`,
      text: `🔥 <b>Визит засчитан — 1/5 на этой неделе</b>\n\nПриходи ещё 2 раза до воскресенья и получи <b>+${amt} HUcoin</b> 🪙`,
    };
    case 2: return {
      title: '🔥 Визит 2/5',
      body: `Ещё 1 визит — и +${amt} HUcoin`,
      text: `🔥 <b>Визит 2/5 на этой неделе</b>\n\nОсталось всего <b>1 посещение</b> до <b>+${amt} HUcoin</b> 🪙`,
    };
    case 3: return {
      title: `🎉 +${amt} HUcoin!`,
      body: `3 визита за неделю. Ещё 2 — и ещё +${amt}`,
      text: `🎉 <b>+${amt} HUcoin за 3 визита на неделе!</b>\n\nДойди до <b>5 визитов</b> — и получишь ещё <b>+${amt}</b> 🪙`,
    };
    case 4: return {
      title: '🔥 Визит 4/5',
      body: `Ещё 1 визит — и ещё +${amt} HUcoin`,
      text: `🔥 <b>Визит 4/5 на этой неделе</b>\n\nОдин шаг до максимума: ещё <b>1 посещение</b> — и <b>+${amt} HUcoin</b> 🪙`,
    };
    case 5: return {
      title: `🏆 +${amt} HUcoin!`,
      body: 'Максимум недели — 5 визитов',
      text: `🏆 <b>+${amt} HUcoin — максимум недели!</b>\n\n5 визитов, всего <b>3 000 HUcoin</b> за неделю.${streak > 1 ? `\nСерия: 🔥 ${streak} ${plural(streak, 'неделя', 'недели', 'недель')} подряд` : ''}`,
    };
    default: return null;
  }
}

/**
 * Called after a check-in. Notifies the player only when the check-in added a
 * new visit day to the CURRENT week (a 2nd tournament on the same day, or a
 * late check-in for a past week, stays silent).
 * Returns the fresh status (null on failure) so the admin UI can show the result.
 */
export async function afterCheckIn(userId: number, visitsBefore: number | null): Promise<LoyaltyStatus | null> {
  try {
    const s = await getLoyaltyStatus(userId);
    if (visitsBefore !== null && s.visits > visitsBefore) {
      const msg = checkInMessage(s.visits, s.streak);
      if (msg) notifyLoyalty(userId, msg.text, msg.title, msg.body);
    }
    return s;
  } catch {
    return null;
  }
}

/** Admin note after a check-in that added a visit day — only when a bonus was just reached. */
export function loyaltyCheckInNote(s: LoyaltyStatus | null | undefined): string | null {
  if (!s || !(LOYALTY_TIERS as readonly number[]).includes(s.visits) || !s.week_rewards.includes(s.visits)) return null;
  return `🔥 ${s.visits}-й визит за неделю — начислено +${LOYALTY_REWARD.toLocaleString('ru-RU')} HUcoin. Можно поздравить игрока!`;
}
